import { Injectable } from '@angular/core';
import { SessionService } from '../services/session-service.service';
import * as Parse from 'parse';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ClientName } from '../components/name-entry/name-entry.component';
import { CookieService } from 'ngx-cookie-service';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { VetBookerError } from '../interfaces/vberror';
import { VbClient } from '@appyvet/vetbooker-definitions/definitions/interfaces/client';
import { AddressObject } from '@appyvet/vetbooker-definitions/dist/register_user';
import {
  UpdateAddressClient,
  UpdateNameClient,
} from '@appyvet/vetbooker-definitions/dist/client';
import {
  BaseVbContact,
  VbContact,
} from '@appyvet/vetbooker-definitions/dist/contacts_model';
import { ParseErrorHandler } from '../parse-error-handler';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { ExtendedAddressObject } from '../interfaces/extended-address.interface';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  CONTACT_VALIDATION_MESSAGES = {
    number: [
      { type: 'required', message: 'You must enter contact information' },
      {
        type: 'pattern',
        message: "Oops, this doesn't look like a valid number",
      },
      {
        type: 'email',
        message: "Oops, this doesn't look like a valid email address",
      },
    ],
    name: [
      { type: 'required', message: 'You must enter a name for this contact' },
    ],
  };
  contacts$ = new BehaviorSubject<VbContact[]>(null);
  client$ = new BehaviorSubject<VbClient>(null);
  private client: VbClient;
  private contacts: VbContact[];
  private clinicCode: string;
  clinicName: string;
  marketingAllowed: boolean;
  private mobileRegexp: string;
  private phoneRegexp: string;
  contactEmail: string;

  constructor(
    private sessionService: SessionService,
    private gaService: GoogleAnalyticsService,
    private cookieService: CookieService,
  ) {
    sessionService.clientPatientDetails$.subscribe((clientPatientDetails) => {
      if (clientPatientDetails) {
        if (clientPatientDetails.marketingSettings) {
          this.marketingAllowed =
            clientPatientDetails.marketingSettings.disableMarketing;
        } else {
          this.marketingAllowed = false;
        }
        this.clinicName =
          clientPatientDetails.themeSettings?.customName || 'VetBooker';
        this.client = clientPatientDetails.clientData.client;
        this.clinicCode = clientPatientDetails.clinicCode;
        this.contacts = clientPatientDetails.contacts;
        this.mobileRegexp = clientPatientDetails.country.mobileRegex;
        this.phoneRegexp = clientPatientDetails.country.phoneRegex;
        this.contactEmail = clientPatientDetails.userDetails.contactEmail;
        this.contacts$.next(this.contacts);
        this.client$.next(this.client);
      }
    });
  }

  getNumberValidator(type: number): ValidatorFn {
    if (type === 1) {
      return Validators.email;
    } else if (type === 3) {
      return Validators.pattern(this.mobileRegexp);
    } else if (type === 0) {
      // A generic telephone (Home/Business) can legitimately hold either a
      // landline or a mobile number, so accept a match against either regex.
      return this.phoneOrMobileValidator();
    }
    return Validators.required;
  }

  /**
   * Validator that passes when the value matches EITHER the landline phone
   * regex OR the mobile regex. Returns a `pattern` error (so the existing
   * "valid number" message is shown) when neither matches.
   */
  private phoneOrMobileValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) {
        return null;
      }
      const matchesPhone = this.phoneRegexp
        ? new RegExp(this.phoneRegexp).test(value)
        : false;
      const matchesMobile = this.mobileRegexp
        ? new RegExp(this.mobileRegexp).test(value)
        : false;
      return matchesPhone || matchesMobile
        ? null
        : { pattern: { requiredPattern: this.phoneRegexp, actualValue: value } };
    };
  }


  updateAddress$(newAddress: ExtendedAddressObject): Observable<boolean> {
    const saveClientRequest = new Subject<boolean>();
    const originalClient = Object.assign({}, this.client);

    // Create base update client object with standard fields
    const updateClient: any = {
      address1: newAddress.address1,
      address2: newAddress.address2,
      address3: newAddress.address3 || newAddress.town, // Use address3 if available, fallback to town
      state: newAddress.state || newAddress.county, // Use state if available, fallback to county
      postcode: newAddress.postcode,
      previousData: originalClient,
    };

    // Add all PMS-specific fields if they exist in the newAddress object
    const pmsSpecificFields = [
      'nameNo',
      'hNameNo',
      'streetAddress',
      'streetAddress2',
      'secondLine',
      'thirdLine',
      'street',
      'locality',
      'suburb',
      'city',
      'zipCode',
      'zipcode',
      'postCode',
      'address4',
    ];

    // Use `!== undefined` so cleared fields (empty string) are also sent.
    pmsSpecificFields.forEach((field) => {
      if (newAddress[field] !== undefined) {
        updateClient[field] = newAddress[field];
      }
    });

    Parse.Cloud.run('saveClientAddress', updateClient, {
      sessionToken: this.cookieService.get('sessionToken'),
    }).then(
      () => {
        // Update local client object with all the new values
        this.client.address1 = newAddress.address1;
        this.client.address2 = newAddress.address2;
        this.client.address3 = newAddress.address3 || newAddress.town;
        this.client.postcode = (newAddress.postcode || '').toUpperCase();
        this.client.state = newAddress.state || newAddress.county;

        // Update PMS-specific fields in local client object
        pmsSpecificFields.forEach((field) => {
          if (newAddress[field] !== undefined) {
            (this.client as any)[field] = newAddress[field];
          }
        });

        this.client$.next(this.client);
        saveClientRequest.next(true);
        this.gaService.event('Edit Address', 'Client Details', this.clinicName);
      },
      (error) => {
        saveClientRequest.error(error);
      },
    );
    return saveClientRequest;
  }

  updateName$(clientName: ClientName): Observable<boolean> {
    const saveClientRequest = new Subject<boolean>();
    const originalClient = Object.assign({}, this.client);
    const updateClient: UpdateNameClient = {
      title: clientName.title,
      firstName: clientName.firstName,
      surname: clientName.surname,
      previousData: originalClient,
    };
    Parse.Cloud.run('saveClientName', updateClient, {
      sessionToken: this.cookieService.get('sessionToken'),
    }).then(
      () => {
        this.client.title = clientName.title;
        this.client.firstName = clientName.firstName;
        this.client.surname = clientName.surname;
        this.client$.next(this.client);
        saveClientRequest.next(true);
        this.gaService.event('Edit Name', 'Client Details', this.clinicName);
      },
      (error) => saveClientRequest.error(error),
    );
    return saveClientRequest;
  }

  addContact(newContact: VbContact) {
    this.contacts.push(newContact);
    this.contacts$.next(this.contacts);
  }

  updateContacts(updatedContact: VbContact, isPreferred: boolean) {
    this.contacts.forEach((contact, index) => {
      if (updatedContact.isPreferred) {
        this.contacts[index].isPreferred = false;
      }
      if (
        contact.id === updatedContact.id &&
        contact.displayName === updatedContact.displayName
      ) {
        if (isPreferred) {
          updatedContact.isPreferred = true;
        }
        updatedContact.allowEmail = updatedContact.optIn;
        updatedContact.allowAMM = updatedContact.optIn;
        if (
          (updatedContact.typeCode === null ||
            updatedContact.typeCode === undefined) &&
          contact.typeCode
        ) {
          updatedContact.typeCode = contact.typeCode;
        }
        this.contacts[index] = updatedContact;
      }
    });
    this.contacts$.next(this.contacts);
  }

  removeContact(deletedContact: VbContact) {
    this.contacts.forEach((contact, index) => {
      if (contact.id === deletedContact.id) {
        this.contacts.splice(index, 1);
      }
    });
    this.contacts$.next(this.contacts);
  }

  async setPreferredContact(contact: VbContact) {
    try {
      await Parse.Cloud.run(
        'setPreferredContactEntry',
        {
          contactEntry: contact,
        },
        { sessionToken: this.cookieService.get('sessionToken') },
      );
      contact.isPreferred = true;
      this.updateContacts(contact, true);
    } catch (e) {
      throw ParseErrorHandler.handleParseError(e);
    }
  }

  updateContact$(contact: VbContact): Observable<boolean> {
    const updateContactRequest$ = new Subject<boolean>();

    const sanitizedContact = {
      ...contact,
      typeCode:
        contact.typeCode ||
        this.getTypeCodeFromDisplayName(contact.displayName),
      isPreferred: Boolean(contact.isPreferred),
      allowEmail: contact.allowEmail || false,
      allowAMM: contact.allowAMM || false,
      optIn: contact.optIn || false,
      type: contact.type || 'General',
      displayName: contact.displayName || 'Home',
    };

    const cleanContact: any = {};
    Object.keys(sanitizedContact).forEach((key) => {
      if (
        sanitizedContact[key] !== null &&
        sanitizedContact[key] !== undefined
      ) {
        cleanContact[key] = sanitizedContact[key];
      }
    });

    Parse.Cloud.run(
      'saveContactEntry',
      {
        contactEntry: cleanContact,
      },
      { sessionToken: this.cookieService.get('sessionToken') },
    ).then(
      () => {
        this.updateContacts(contact, false);
        this.gaService.event('Edit Contact', 'Client Details', this.clinicName);
        updateContactRequest$.next(true);
      },
      (error) => {
        updateContactRequest$.error(error);
      },
    );
    return updateContactRequest$;
  }

  addContact$(newContact: BaseVbContact): Observable<boolean> {
    const addContactRequest$ = new Subject<boolean>();
    Parse.Cloud.run('addContactEntry', newContact, {
      sessionToken: this.cookieService.get('sessionToken'),
    }).then(
      (contact) => {
        this.addContact(contact);
        addContactRequest$.next(true);
        let contactType = 'Home';
        if (contact.typeCode === 1) {
          contactType = 'Email';
        }
        if (contact.typeCode === 3) {
          contactType = 'Mobile';
        }
        this.gaService.event(
          'Add ' + contactType + ' Contact',
          'Client Details',
          this.clinicName,
        );
      },
      (error) => {
        addContactRequest$.error(JSON.parse(error.error) as VetBookerError);
      },
    );
    return addContactRequest$;
  }

  deleteContact$(contact: VbContact): Observable<boolean> {
    const deleteContactRequest$ = new Subject<boolean>();
    Parse.Cloud.run(
      'deleteContactEntry',
      {
        contactEntry: contact,
      },
      { sessionToken: this.cookieService.get('sessionToken') },
    ).then(
      () => {
        this.removeContact(contact);
        deleteContactRequest$.next(true);
      },
      (error) => {
        deleteContactRequest$.error(error);
      },
    );
    return deleteContactRequest$;
  }

  getTypeCodeFromDisplayName(displayName: string): number {
    if (!displayName) {
      return 0;
    }
    const lowerDisplayName = displayName.toLowerCase();
    if (
      lowerDisplayName.includes('email') ||
      lowerDisplayName.includes('online')
    ) {
      return 1;
    } else if (lowerDisplayName.includes('mobile')) {
      return 3;
    } else {
      return 0;
    }
  }
}
